import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";

// The universal component created earlier:
import GenericInfoPanel from "../../../../../../components/Modals/GenericInfoPanel";

// Configuration objects for customer contacts.
import {
  contactGeneralFields,
  contactAdditionalFields,
  initialContactFormData,
} from "../../config/customerConfig";

// Configuration objects for buildings.
import {
  BuildingFields,
  AddressFields,
  AdditionalInfoFields,
  initialBuildingFormData,
} from "../../../Building/config/formDataConfig";

// API service functions.
import {
  updateContact,
  addNewContact,
} from "../../services/customerContactService";
import {
  updateBuilding,
  createNewBuilding,
} from "../../services/customerBuildingService";

// Redux actions for the selected customer contact.
import {
  selectedSelectedCustomerContact,
  setSelectedCustomerContact,
  clearSelectedCustomerContact,
} from "../../../../../../redux/customers/selectedCustomerContactSlice";

// Redux actions for customer contacts list.
import {
  updateAssignedContact,
  addAssignedContact,
} from "../../../../../../redux/customers/customerContactsSlice";

// Redux actions for the selected customer building.
import {
  selectSelectedCustomerBuilding,
  setSelectedCustomerBuilding,
  clearSelectedCustomerBuilding,
} from "../../../../../../redux/customers/selectedCustomerBuildingSlice";

// Redux actions for customer buildings list.
import {
  updateBuildingInState,
  addBuildingToState,
} from "../../../../../../redux/customers/customerBuildingSlice";

const CustomerInformationDisplay = ({
  isEditMode,
  newMode, // true if a new entity is being created
  newEntityType, // "contact" or "building" (only used when newMode is true)
  onCloseNewMode, // Callback to exit new mode (e.g. on cancel or after submit)
  updateList, // Callback to refresh the list after submit
}) => {
  const dispatch = useDispatch();

  // Get the selected customer contact/building from Redux.
  const selectedContact = useSelector(selectedSelectedCustomerContact);
  const selectedBuilding = useSelector(selectSelectedCustomerBuilding);

  // Local state to hold form data for a new or existing entity.
  const [formData, setFormData] = useState(null);

  // Initialize new form data when newMode is active or load the selected entity.
  useEffect(() => {
    if (newMode) {
      if (newEntityType === "contact") {
        setFormData(initialContactFormData);
        dispatch(clearSelectedCustomerContact());
      } else if (newEntityType === "building") {
        setFormData(initialBuildingFormData);
        dispatch(clearSelectedCustomerBuilding());
      }
    } else {
      if (selectedContact && selectedContact.contactName !== undefined) {
        setFormData(selectedContact);
      } else if (
        selectedBuilding &&
        selectedBuilding.buildingReference !== undefined
      ) {
        setFormData(selectedBuilding);
      } else {
        setFormData(null);
      }
    }
  }, [newMode, newEntityType, dispatch, selectedContact, selectedBuilding]);

  // Handle changes to form fields.
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (!newMode) {
      if (selectedContact && selectedContact.contactName !== undefined) {
        const updated = { ...selectedContact, [name]: value };
        dispatch(setSelectedCustomerContact(updated));
      } else if (
        selectedBuilding &&
        selectedBuilding.buildingReference !== undefined
      ) {
        const updated = { ...selectedBuilding, [name]: value };
        dispatch(setSelectedCustomerBuilding(updated));
      }
    }
  };

  // Determine which entity we’re working with.
  let entityType = null;
  if (newMode) {
    entityType = newEntityType;
  } else if (selectedContact && selectedContact.contactName !== undefined) {
    entityType = "contact";
  } else if (
    selectedBuilding &&
    selectedBuilding.buildingReference !== undefined
  ) {
    entityType = "building";
  }

  if (!formData || !entityType) {
    return (
      <div className="text-center text-gray-500">
        <p>No contact or building selected.</p>
      </div>
    );
  }

  // Configure title and field sections based on the entity type.
  let title = "";
  let fieldSections = [];
  if (entityType === "contact") {
    title = newMode ? "New Contact" : `Contact - ${formData.contactName}`;
    fieldSections = [
      { label: "General", fields: contactGeneralFields },
      { label: "Additional Info", fields: contactAdditionalFields },
    ];
  } else if (entityType === "building") {
    title = newMode
      ? "New Building"
      : `Building - ${formData.buildingReference}`;
    fieldSections = [
      { label: "General", fields: BuildingFields },
      { label: "Address", fields: AddressFields },
      { label: "Additional Info", fields: AdditionalInfoFields },
    ];
  }

  // Submit handler to either create a new entity or update an existing one.
  const handleSubmit = async () => {
    try {
      let updatedEntity;
      if (newMode) {
        // Creating a new entity.
        if (entityType === "contact") {
          updatedEntity = await addNewContact(formData);
          dispatch(addAssignedContact(updatedEntity));
        } else if (entityType === "building") {
          updatedEntity = await createNewBuilding(formData);
          dispatch(addBuildingToState(updatedEntity));
        }
      } else {
        // Updating an existing entity.
        if (entityType === "contact") {
          updatedEntity = await updateContact(formData.contactID, formData);
          dispatch(
            updateAssignedContact({
              contactID: formData.contactID,
              updates: updatedEntity,
            })
          );
          dispatch(setSelectedCustomerContact(updatedEntity));
        } else if (entityType === "building") {
          updatedEntity = await updateBuilding(formData.buildingID, formData);
          dispatch(updateBuildingInState(updatedEntity));
          dispatch(setSelectedCustomerBuilding(updatedEntity));
        }
      }
      // Refresh the list and close the new/edit mode.
      updateList();
      onCloseNewMode();
    } catch (error) {
      console.error("Submit failed:", error);
      alert(`Submit failed: ${error.message}`);
    }
  };

  return (
    <GenericInfoPanel
      title={title}
      fieldSections={fieldSections}
      formData={formData}
      isEditMode={isEditMode}
      newMode={newMode}
      onChange={handleChange}
      onSubmit={handleSubmit}
      onCancel={onCloseNewMode}
    />
  );
};

export default CustomerInformationDisplay;
